/**
 * Deduction Hook
 * 일차감 페이지 데이터 관리 및 API 호출 로직
 */

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { getDeductionData, getSupplierDeductionData, getDeductTypeCodes } from '../api/deductionApi';
import { getUserInfo } from '../../../core/utils';
import {
    DeductionRequest,
    DeductionItem,
    DeductTypeCode,
} from '../types/deduction';

/**
 * 일차감 페이지 Hook
 */
export const useDeduction = () => {
    // 상태 관리
    const [deductionList, setDeductionList] = useState<DeductionItem[]>([]);
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [deductTypeCodes, setDeductTypeCodes] = useState<DeductTypeCode[]>([]);
    const [deductType, setDeductType] = useState<string>('');
    const [deductName, setDeductName] = useState<string>('');
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    
    // API 호출 상태 관리
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    // 사용자 정보
    const userInfo = getUserInfo();
    const isSupplier = !!userInfo?.suppId;
    const riderId = useMemo(() => userInfo?.riderId || '', [userInfo]);
    const suppId = useMemo(() => userInfo?.suppId || '', [userInfo]);
    
    // 날짜 변경 추적을 위한 ref
    const lastChangedRef = useRef<'from' | 'to' | null>(null);

    // 내부 공통 포맷터: Date -> YYYY-MM-DD
    const formatDateYmd = useCallback((date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }, []);

    // 내부 공통 파서: YYYY-MM-DD -> Date
    const parseDateYmd = useCallback((ymd: string) => {
        const parts = ymd.split('-');
        if (parts.length !== 3) return new Date(NaN);
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // 0-based month
        const day = parseInt(parts[2], 10);
        return new Date(year, month, day);
    }, []);

    /**
     * 공제 타입 코드 조회
     */
    const fetchDeductTypeCodes = useCallback(async () => {
        const responseData = await getDeductTypeCodes();
        if (responseData.success) {
            setDeductTypeCodes(responseData.deductTypeCodes);
        }
    }, []);

    /**
     * 일차감 페이지 초기화 (초기 날짜 설정)
     */
    const initializeDeduction = useCallback(async () => {
        await fetchDeductTypeCodes();
        
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        
        setFromDate(formatDateYmd(sevenDaysAgo));
        setToDate(formatDateYmd(today));
        setIsInitialized(true);
    }, [formatDateYmd, fetchDeductTypeCodes]);

    /**
     * 일차감 데이터 조회
     */
    const fetchDeductionData = useCallback(async () => {
        if (!fromDate || !toDate || !isInitialized) return;

        setIsLoading(true);
        setError(null);

        try {
            let responseData;
            
            if (isSupplier) {
                // 협력사: 소속 모든 라이더 일차감 조회
                responseData = await getSupplierDeductionData(fromDate, toDate);
            } else {
                // 라이더: 본인 일차감만 조회
                responseData = await getDeductionData(fromDate, toDate);
            }
            
            if (responseData.success) {
                const list = responseData.deductList || responseData.deductDailyList || [];
                setDeductionList(list);
            }
        } catch (error) {
            setError(error as Error);
        } finally {
            setIsLoading(false);
        }
    }, [fromDate, toDate, deductType, suppId, riderId, isInitialized, isSupplier]);

    // 날짜 변경 핸들러
    const handleFromDateChange = useCallback((value: string) => {
        setFromDate(value);
        lastChangedRef.current = 'from';
    }, []);

    const handleToDateChange = useCallback((value: string) => {
        setToDate(value);
        lastChangedRef.current = 'to';
    }, []);

    // 공제 타입 변경 핸들러
    const handleDeductTypeChange = useCallback((value: string) => {
        setDeductType(value);
    }, []);

    // 공제 이름 변경 핸들러
    const handleDeductNameChange = useCallback((value: string) => {
        setDeductName(value);
    }, []);

    // 초기화 실행
    useEffect(() => {
        initializeDeduction();
    }, [initializeDeduction]);

    // 날짜나 필터가 변경될 때마다 데이터 조회
    useEffect(() => {
        if (isInitialized && fromDate && toDate) {
            fetchDeductionData();
        }
    }, [fromDate, toDate, deductType, isInitialized, fetchDeductionData]);

    // 공제 타입 및 이름 필터링
    const filteredDeductionList = useMemo(() => {
        let filtered = deductionList;
        
        // 공제 타입 필터링 (comCode로 선택된 값을 comCdName으로 변환하여 deductName과 비교)
        if (deductType) {
            const selectedTypeName = deductTypeCodes.find(code => code.comCode === deductType)?.comCdName;
            if (selectedTypeName) {
                filtered = filtered.filter(item => item.deductName === selectedTypeName);
            }
        }
        
        // 공제 이름 필터링
        if (deductName) {
            filtered = filtered.filter(item => item.deductName === deductName);
        }
        
        return filtered;
    }, [deductionList, deductType, deductName, deductTypeCodes]);

    return {
        // 상태
        deductionList,
        fromDate,
        toDate,
        deductTypeCodes,
        deductType,
        deductName,
        isInitialized,
        filteredDeductionList,
        
        // 로딩 상태
        isLoading,
        error,
        isSupplier,
        
        // 핸들러
        handleFromDateChange,
        handleToDateChange,
        handleDeductTypeChange,
        handleDeductNameChange,
        
        // 유틸리티
        formatDateYmd,
        parseDateYmd,
    };
};
